import { type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { ConfigChip } from './ConfigChip';
import { groupOptions, hoistSelectedFirst } from '../shared/select-option-groups';

export interface ConfigChipSelectOption<T> {
  /** Stable React key for the row. */
  key: string;
  /** Value committed via `onChange` when the row is chosen. */
  value: T;
  /** Row content. */
  label: ReactNode;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Optional trailing detail (description, badge, repo, …). */
  trailing?: ReactNode;
  /** cmdk match/identity string used for search. Defaults to `key`. */
  searchValue?: string;
  /**
   * Optional group. Rows sharing a value render together, in first-seen order.
   * A non-empty string renders as a section heading; `undefined` or `''` render
   * a heading-less group (use distinct empties to pin a leading sentinel group
   * above the rest). Selected-first reordering is scoped per group, so a
   * sentinel in its own group never gets bumped by the active selection.
   */
  group?: string;
  /** Render the row unavailable while keeping it visible in the catalog. */
  disabled?: boolean;
}

export interface ConfigChipSelectProps<T> {
  /** Accessible name for the chip trigger. */
  ariaLabel: string;
  /** Closed-chip summary text. */
  triggerLabel: ReactNode;
  /** Closed-chip leading icon. */
  triggerIcon: ReactNode;
  options: ConfigChipSelectOption<T>[];
  value: T;
  onChange: (next: T) => void;
  /** Equality for the selected check + reordering. Defaults to `Object.is`. */
  isSelected?: (optionValue: T, value: T) => boolean;
  /** When set, renders a search box with this placeholder. */
  searchPlaceholder?: string;
  /** Empty-state copy for the search box. Defaults to "No results found.". */
  emptyMessage?: string;
  /** Hoist the selected row to the top of its group. Defaults to `true`. */
  selectedFirst?: boolean;
  disabled?: boolean;
  dashed?: boolean;
  /** Extra classes for the scroll list (e.g. a taller max-height). */
  listClassName?: string;
  /** Explanatory content rendered above the option list. */
  description?: ReactNode;
  'data-testid'?: string;
}

/**
 * Generic single-select dropdown rendered inside a {@link ConfigChip}. Owns the
 * `Command` list, optional search box, per-group section headings, the selected
 * check mark, close-on-select, and selected-first ordering — so each picker
 * (blueprint, devbox size, user mode, …) only supplies its options instead of
 * re-implementing the popover plumbing. Selected-first means the active option
 * is reordered to the top of its group, which is why a "none"/default sentinel
 * lives in its own group: it stays pinned at the top regardless of selection.
 */
export function ConfigChipSelect<T>({
  ariaLabel,
  triggerLabel,
  triggerIcon,
  options,
  value,
  onChange,
  isSelected = Object.is,
  searchPlaceholder,
  emptyMessage = 'No results found.',
  selectedFirst = true,
  disabled,
  dashed,
  listClassName,
  description,
  'data-testid': dataTestId,
}: ConfigChipSelectProps<T>) {
  const groups = selectedFirst
    ? hoistSelectedFirst(groupOptions(options), (opt) => isSelected(opt.value, value))
    : groupOptions(options);

  return (
    <ConfigChip
      ariaLabel={ariaLabel}
      disabled={disabled}
      dashed={dashed}
      label={triggerLabel}
      icon={triggerIcon}
      data-testid={dataTestId}
    >
      {(close) => (
        <Command>
          {searchPlaceholder ? (
            <CommandInput placeholder={searchPlaceholder} className="h-9" />
          ) : null}
          {description ? (
            <div
              className="border-b border-border bg-muted/40 px-3 py-2 text-2xs text-muted-foreground"
              role="note"
            >
              {description}
            </div>
          ) : null}
          <CommandList className={cn('max-h-72', listClassName)}>
            {searchPlaceholder ? <CommandEmpty>{emptyMessage}</CommandEmpty> : null}
            {groups.map((group, groupIndex) => (
              <CommandGroup key={group.heading ?? `__group-${groupIndex}`} heading={group.heading}>
                {group.options.map((opt) => {
                  const selected = isSelected(opt.value, value);
                  const isDisabled = opt.disabled === true;
                  return (
                    <CommandItem
                      key={opt.key}
                      value={opt.searchValue ?? opt.key}
                      disabled={isDisabled}
                      onSelect={() => {
                        if (isDisabled) return;
                        onChange(opt.value);
                        close();
                      }}
                      className={cn('gap-2', isDisabled && 'cursor-not-allowed opacity-50')}
                      aria-disabled={isDisabled || undefined}
                    >
                      {opt.icon}
                      <span className="flex-1 truncate">{opt.label}</span>
                      {opt.trailing}
                      {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      )}
    </ConfigChip>
  );
}
